import React, {useState, useMemo, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  CountrySelectorColumn,
  FormGrid,
  FullWidth,
} from '../../../../accountManagement/features/AccountInfo/AccountInfo.style'
import {CountrySelector} from '../../../../accountManagement/features/CountrySelector/CountrySelector'
import {COUNTRIES} from '../../../../accountManagement/features/CountrySelector/CountrySelector.utils'
import {DynamicField} from '../../../../accountManagement/features/AccountInfo/components/DynamicField'
import {buildFieldConfigMap} from '../../../../accountManagement/features/AccountInfo/config/locationFieldRules'
import {validatePhoneNumber} from '../../../../utils/address-validation-util'
import {
  selectProposalInfo,
  selectRecurlyAccountInfo,
  updateProposalInfo,
} from '../../../../app-store/ProposalSlice'
import {selectEffectiveRole} from '../../../../app-store/selector'

const FIELD_CONFIG = [
  {
    key: 'businessName',
    label: 'Business Name',
    placeholder: 'Business Name',
    type: 'text',
    layout: 'grid',
    validators: ['required'],
  },
  {
    key: 'clientFirstName',
    label: 'First Name',
    placeholder: 'First Name',
    type: 'text',
    layout: 'grid',
    validators: ['alphaName'],
  },
  {
    key: 'clientLastName',
    label: 'Last Name',
    placeholder: 'Last Name',
    type: 'text',
    layout: 'grid',
    validators: ['alphaName'],
  },
  {
    key: 'clientPhone',
    label: 'Mobile Number',
    placeholder: '(###) ###-####',
    type: 'tel',
    layout: 'grid',
    maxLength: 18,
    validators: ['phone'],
  },
  {
    key: 'clientEmail',
    label: 'Email Address',
    placeholder: 'Email Address',
    type: 'email',
    layout: 'grid',
    disabled: true,
    validators: ['required', 'email'],
  },
  {
    key: 'salesRepOrPartner',
    label: 'Is this sale on behalf of another Sales or Partner?',
    type: 'toggle',
    layout: 'left',
    default: 'no',
  },

  {
    key: 'salesRepInfo',
    label: 'Search by Name',
    // placeholder: 'Search by Name',
    type: 'async-search',
    layout: 'left',
    validators: ['required'],
  },

  {
    key: 'xpCode',
    label: 'Search Partner',
    placeholder: 'XP Code',
    type: 'xp-code-search',
    layout: 'left',
    validators: ['required'],
  },
  {
    key: 'overrideSST',
    label: 'Do you want to override SST on this order?',
    type: 'toggle',
    layout: 'left',
    default: 'no',
  },

  {
    key: 'overrideRepInfo',
    label: 'Search by Name',
    // placeholder: 'Search by Name',
    type: 'async-search',
    layout: 'left',
    validators: ['required'],
  },
]

// Fields whose value must be cleared whenever the toggle driving their
// visibility changes — otherwise a stale search result (e.g. an XP Code typed
// under "Legacy Partner") would carry over after switching to another option.
// Mirrors the visibility rules in locationFieldRules.js (salesRepInfo/xpCode
// keyed off salesRepOrPartner, overrideRepInfo keyed off overrideSST).
const DEPENDENT_FIELDS = {
  salesRepOrPartner: ['salesRepInfo', 'xpCode'],
  overrideSST: ['overrideRepInfo'],
}

// ── Validation ────────────────────────────────────────────────────────────────
// Mirrors the same rules used in AccountInfo/utils/formHelpers.js

function runFieldValidators(label, value, fieldValidators, countryCode = 'US') {
  const trimmedValue = typeof value === 'string' ? value.trim() : value

  // REQUIRED
  if (fieldValidators.includes('required')) {
    const isEmpty =
      trimmedValue === undefined || trimmedValue === null || trimmedValue === ''
    if (isEmpty) return 'Required'
  }

  // EMAIL
  if (fieldValidators.includes('email') && trimmedValue) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(trimmedValue)) return 'Invalid email address'
  }

  // PHONE — country-aware, same as formHelpers.js
  if (fieldValidators.includes('phone') && trimmedValue) {
    if (!validatePhoneNumber(countryCode, trimmedValue))
      return 'Invalid phone number'
  }

  // ALPHA NAME — same regex + min-letter rule as formHelpers.js
  if (fieldValidators.includes('alphaName') && trimmedValue) {
    if (!/^[a-zA-Z\s'-]+$/.test(trimmedValue)) {
      return 'Name must contain only letters, spaces, hyphens, or apostrophes'
    }
    const letterCount = trimmedValue.replace(/[^a-zA-Z]/g, '').length
    if (letterCount < 3) return `${label} must contain at least 3 letters`
  }

  return null
}

function validateAllFields(fields, formData, countryCode) {
  const errors = {}
  fields.forEach(field => {
    const error = runFieldValidators(
      field.label,
      formData[field.key],
      field.validators ?? [],
      countryCode,
    )
    if (error) errors[field.key] = error
  })
  return errors
}

// ── Component ─────────────────────────────────────────────────────────────────

export const ProposalAccountInformation = ({
  isExistingAccount,
  onValidityChange,
  variant = 'form',
  size = 'md',
}) => {
  const dispatch = useDispatch()

  // ── Redux state (form data lives in ProposalSlice) ────────────────────────
  const proposalInfo = useSelector(selectProposalInfo)
  const selectedCountry = proposalInfo?.country ?? 'US'
  const recurlyAccountInfo = useSelector(selectRecurlyAccountInfo)
  const roles = useSelector(selectEffectiveRole)

  // ── Local state (errors / touched stay in the component) ─────────────────
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})

  // Dispatch default country ('US') on mount if not already set
  useEffect(() => {
    if (!proposalInfo?.country) {
      dispatch(updateProposalInfo({country: 'US'}))
    }
  }, [])

  // Apply FIELD_CONFIG's `default` (e.g. the salesRepOrPartner/overrideSST
  // toggles default to 'no') for any field that hasn't been set yet — mirrors
  // emptyLocation() in accountManagement/features/AccountInfo/config/locationFields.js
  useEffect(() => {
    const defaults = FIELD_CONFIG.reduce((acc, field) => {
      if (field.default !== undefined && proposalInfo[field.key] == null) {
        acc[field.key] = field.default
      }
      return acc
    }, {})
    if (Object.keys(defaults).length) {
      dispatch(updateProposalInfo(defaults))
    }
  }, [])

  // Pre-populate form fields from recurly account when it loads — existing
  // clients only. New clients have no account yet, so there is nothing to
  // pre-fill and we must not carry over data from a previous session.
  useEffect(() => {
    if (!isExistingAccount) return
    if (Object.keys(recurlyAccountInfo).length === 0) return
    dispatch(
      updateProposalInfo({
        ...proposalInfo,
        clientFirstName: recurlyAccountInfo?.firstName ?? '',
        clientLastName: recurlyAccountInfo?.lastName ?? '',
        clientEmail: recurlyAccountInfo?.email ?? '',
        clientPhone: recurlyAccountInfo?.address?.phone ?? '',
        businessName: `${recurlyAccountInfo?.firstName ?? ''} ${
          recurlyAccountInfo?.lastName ?? ' '
        }`.trim(),
        country: recurlyAccountInfo?.address?.country ?? '',
      }),
    )
  }, [recurlyAccountInfo])

  // ── Field config ──────────────────────────────────────────────────────────

  const fieldConfigMap = useMemo(
    () =>
      buildFieldConfigMap({roles, account: proposalInfo, values: proposalInfo}),
    [roles, proposalInfo],
  )

  const resolvedFields = useMemo(
    () =>
      FIELD_CONFIG.map(f => ({
        ...f,
        ...(fieldConfigMap[f.key] || {}),
      })).filter(f => f.visible !== false),
    [fieldConfigMap],
  )

  // ── Validity ──────────────────────────────────────────────────────────────

  const isFormValid = useMemo(() => {
    if (!selectedCountry) return false
    const allErrors = validateAllFields(
      resolvedFields,
      proposalInfo,
      selectedCountry,
    )
    return Object.keys(allErrors).length === 0
  }, [selectedCountry, proposalInfo, resolvedFields])

  useEffect(() => {
    onValidityChange?.(isFormValid)
  }, [isFormValid, onValidityChange])

  // ── Handlers ──────────────────────────────────────────────────────────────

  // Mirrors AccountInfo updateParent: update value in Redux + validate immediately
  const handleChange = (fieldKey, value) => {
    // Switching salesRepOrPartner/overrideSST invalidates whatever was
    // previously searched/entered in their dependent field(s) — clear them
    // rather than leaving a stale rep/XP Code behind.
    const dependents =
      proposalInfo[fieldKey] !== value ? DEPENDENT_FIELDS[fieldKey] ?? [] : []
    const clearedValues = dependents.reduce((acc, key) => {
      acc[key] = null
      return acc
    }, {})

    dispatch(updateProposalInfo({[fieldKey]: value, ...clearedValues}))

    if (dependents.length) {
      setErrors(prev => {
        const next = {...prev}
        dependents.forEach(key => delete next[key])
        return next
      })
      setTouched(prev => {
        const next = {...prev}
        dependents.forEach(key => delete next[key])
        return next
      })
    }

    const field = resolvedFields.find(f => f.key === fieldKey)
    if (!field) return

    const error = runFieldValidators(
      field.label,
      value,
      field.validators ?? [],
      selectedCountry ?? 'US',
    )
    setErrors(prev => {
      const next = {...prev}
      if (error) {
        next[fieldKey] = error
      } else {
        delete next[fieldKey]
      }
      return next
    })
  }

  // Mirrors AccountInfo validateParentField: mark touched + validate on blur
  const handleBlur = fieldKey => {
    setTouched(prev => ({...prev, [fieldKey]: true}))

    const field = resolvedFields.find(f => f.key === fieldKey)
    if (!field) return

    const error = runFieldValidators(
      field.label,
      proposalInfo[fieldKey],
      field.validators ?? [],
      selectedCountry ?? 'US',
    )
    setErrors(prev => {
      const next = {...prev}
      if (error) {
        next[fieldKey] = error
      } else {
        delete next[fieldKey]
      }
      return next
    })
  }

  // Re-validate country-sensitive fields with a value when country changes —
  // phone validation is country-aware (same pattern as useAccountTree.js's
  // COUNTRY_SENSITIVE_FIELDS effect). Not gated by touched: a value can be
  // present (typed or autofilled) without the field ever having been
  // touched, and its error needs to reflect the new country regardless.
  const handleCountryChange = country => {
    dispatch(updateProposalInfo({...proposalInfo, country}))

    setErrors(prev => {
      const next = {...prev}
      resolvedFields.forEach(field => {
        if (!proposalInfo[field.key]) return
        const error = runFieldValidators(
          field.label,
          proposalInfo[field.key],
          field.validators ?? [],
          country ?? 'US',
        )
        if (error) {
          next[field.key] = error
        } else {
          delete next[field.key]
        }
      })
      return next
    })
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <CountrySelectorColumn>
        <CountrySelector
          value={selectedCountry}
          onChange={handleCountryChange}
          disabled={isExistingAccount}
          countries={COUNTRIES}
        />
      </CountrySelectorColumn>
      <hr />
      <FormGrid>
        {resolvedFields.map(field => {
          const fieldElement = (
            <DynamicField
              key={field.key}
              field={field}
              value={proposalInfo[field.key] ?? ''}
              values={proposalInfo}
              error={errors[field.key]}
              touched={touched[field.key]}
              // Disabled until country is selected — mirrors AccountInfo.js
              disabled={!selectedCountry || field.disabled}
              variant={variant}
              size={size}
              onChange={handleChange}
              onBlur={handleBlur}
              country={selectedCountry}
            />
          )

          if (field.layout === 'full') {
            return <FullWidth key={field.key}>{fieldElement}</FullWidth>
          }

          return <React.Fragment key={field.key}>{fieldElement}</React.Fragment>
        })}
      </FormGrid>
    </>
  )
}
